import { Card, OnboardingLoading, DashStyles as Styles } from '../../components/dashboard/DashboardUI';
import { Constants } from '../../internal/Constants';
import { Localized } from '../../internal/Localized';
import { useDashboard } from '../../providers/DashboardProvider';
import { useLobbySettings } from '../../providers/LobbySettingsProvider';
import { useLocale } from '../../providers/LocaleProvider';
import { Button, Label, useAuth } from '@app/engine';
import { CommonConstants, FormatLocalized, GetCurrencyForCountry, Logger, ResolveCurrencyAmount, ResolveSupportedCurrency } from '@shared/common';
import { BillingClient, Telemetry } from '@shared/frontend';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { IPlanDTO } from '@shared/common';

/** File-local constants for the billing-setup gate. */
const localConstants = {
  logContext: 'BillingStep',
  minorUnitsPerMajor: 100,
  checkoutParam: 'checkout',
  checkoutSuccess: 'success',
  checkoutCancel: 'cancel',
  // Funnel step key reported once billing verifies (the wizard's persisted billing gate step).
  telemetryStepBilling: CommonConstants.Database.OnboardingStep.Billing,
  // Bounded reconcile poll for the post-checkout return. The subscription is created by Stripe and persisted by the billing webhook, which normally lands within a few seconds. Poll a few times
  // before surfacing a "still processing" message rather than blocking forever or giving up after a single miss.
  reconcileMaxAttempts: 6,
  reconcileDelayMs: 2500
} as const;

/**
 * Resolves a plan's recurring base amount in the visitor's billing currency.
 *
 * @param plan - The plan whose base amount to resolve.
 * @param countryCode - The tenant's selected country, used to resolve the billing currency the price will settle in (falls back to the plan's default currency when absent or unsupported).
 * @returns The minor-unit base amount and the currency it settles in.
 */
function ResolvePlanBase(plan: IPlanDTO, countryCode: string) {
  // Resolve the billing currency from the tenant's country so the summary matches what Stripe charges. The subscription that carries the settlement currency does not exist yet here.
  const visitorCurrency = countryCode ? GetCurrencyForCountry(countryCode) : plan.currency;
  const effectiveCurrency = ResolveSupportedCurrency(plan.baseAmountByCurrency, plan.currency, visitorCurrency);
  const amountMinor = ResolveCurrencyAmount(plan.baseAmount, plan.baseAmountByCurrency, plan.currency, effectiveCurrency);
  return { amountMinor, effectiveCurrency };
}

/**
 * Formats a plan's recurring base amount as a currency string in the user's selected app language.
 *
 * @param plan - The plan whose base price to format.
 * @param locale - The user's selected app locale (BCP-47), so separators and symbol placement follow the in-app language rather than the browser locale.
 * @param countryCode - The tenant's selected country, used to resolve the billing currency the price will settle in (falls back to the plan's default currency when absent or unsupported).
 * @returns The formatted price (falls back to "<amount> <CURRENCY>" when Intl rejects the currency).
 */
function FormatPlanPrice(plan: IPlanDTO, locale: string, countryCode: string): string {
  const { amountMinor, effectiveCurrency } = ResolvePlanBase(plan, countryCode);
  const value = amountMinor / localConstants.minorUnitsPerMajor;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: effectiveCurrency.toUpperCase() }).format(value);
  } catch {
    return `${value.toFixed(2)} ${effectiveCurrency.toUpperCase()}`;
  }
}

/**
 * Billing step of the onboarding wizard: the second gated step. The account step created the tenant but no subscription, so a freshly onboarded tenant has no billing access (status === null) and
 * this step starts hosted Stripe checkout. The wizard advances to the phone step only once billing verifies — the billing webhook persists a trialing subscription, which the dashboard provider
 * reads back as a non-null status. Starting checkout carries no immediate charge: the card authorises the trial and is only billed once the trial ends, and the plan can be cancelled at any
 * time.
 *
 * @param props - Component props.
 * @param props.tenantID - The onboarding tenant to bill (resolved by the wizard from the onboarding status).
 * @returns The billing-step panel element.
 */
export function BillingStep({ tenantID }: { tenantID: string }) {
  const { refreshBilling } = useDashboard();
  const { contactEmail } = useLobbySettings();
  const supportEmail = contactEmail ?? '';
  const { selectedLang } = useLocale();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutResult = searchParams.get(localConstants.checkoutParam);

  const [plan, setPlan] = useState<IPlanDTO | null>(null);
  // The tenant's country, stashed by the account step. Drives the price summary currency since no subscription (which would carry the settlement currency) exists yet at this gate.
  const [billingCountry] = useState(() => sessionStorage.getItem(Constants.SetupBillingCountryStorageKey) ?? '');
  const [redirecting, setRedirecting] = useState(false);
  const [finalizing, setFinalizing] = useState(checkoutResult === localConstants.checkoutSuccess);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(checkoutResult === localConstants.checkoutCancel ? Localized.SetupBillingCancelled : '');
  const finalizeStartedRef = useRef(false);
  const cancelTrackedRef = useRef(false);

  // Funnel signal for the checkout-cancel return, emitted once per mount (the cancel notice itself is derived state and re-renders freely).
  useEffect(() => {
    if (checkoutResult !== localConstants.checkoutCancel || cancelTrackedRef.current) return;
    cancelTrackedRef.current = true;
    Telemetry.CheckoutOutcome(Telemetry.Outcome.Cancel);
  }, [checkoutResult]);

  // Resolve the plan to bill: prefer the one selected during onboarding (stashed for this handoff), else the featured plan, else the cheapest. Used both to start checkout and to show the price.
  useEffect(() => {
    const controller = new AbortController();
    BillingClient.ListPublicPlans(controller.signal, selectedLang)
      .then((plans) => {
        if (controller.signal.aborted) return;
        const stored = sessionStorage.getItem(Constants.SetupBillingPlanStorageKey) ?? '';
        setPlan(plans.find((entry) => entry.code === stored) ?? plans.find((entry) => entry.featured) ?? plans[0] ?? null);
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      });
    return () => controller.abort();
  }, [selectedLang]);

  // Post-checkout return: poll the billing service until the webhook-created subscription reconciles (status flips off null), then refresh the gate so it advances to the dashboard. Bounded so a
  // delayed or missed webhook surfaces an actionable message instead of spinning forever.
  useEffect(() => {
    if (checkoutResult !== localConstants.checkoutSuccess || !tenantID) return;
    if (finalizeStartedRef.current) return;
    finalizeStartedRef.current = true;
    let cancelled = false;
    const controller = new AbortController();
    const poll = async () => {
      for (let attempt = 0; attempt < localConstants.reconcileMaxAttempts; attempt++) {
        try {
          const access = await BillingClient.RefreshAccess(controller.signal, tenantID);
          if (cancelled) return;
          if (access.status !== null) {
            Telemetry.CheckoutOutcome(Telemetry.Outcome.Success);
            Telemetry.OnboardingStepCompleted(localConstants.telemetryStepBilling);
            refreshBilling();
            return;
          }
        } catch (err: unknown) {
          if (cancelled) return;
          Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
        }
        await new Promise((resolve) => setTimeout(resolve, localConstants.reconcileDelayMs));
        if (cancelled) return;
      }
      if (!cancelled) {
        Telemetry.CheckoutOutcome(Telemetry.Outcome.Failed);
        setFinalizing(false);
        setError(FormatLocalized(Localized.SetupBillingTimeout, supportEmail));
      }
    };
    void poll();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [checkoutResult, tenantID, refreshBilling, supportEmail]);

  const startCheckout = useCallback(async () => {
    if (redirecting || !tenantID || !plan) return;
    setRedirecting(true);
    setError('');
    setNotice('');
    try {
      const controller = new AbortController();
      const { url } = await BillingClient.CreateCheckoutSession(controller.signal, { tenantID, planCode: plan.code });
      Telemetry.CheckoutOutcome(Telemetry.Outcome.Started);
      // Full-page handoff to the provider-hosted checkout. On completion Stripe returns to BILLING_CHECKOUT_SUCCESS_URL (resolves to the BillingReturn route), where this gate finalizes.
      window.location.href = url;
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
      setError(FormatLocalized(Localized.SetupBillingError, supportEmail));
      setRedirecting(false);
    }
  }, [redirecting, tenantID, plan, supportEmail]);

  // Abandons onboarding: signs the visitor out and returns them to the public landing page.
  const onLogout = useCallback(async () => {
    try {
      await logout();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, Logger.GetErrorMessage(err));
    }
    navigate(Constants.Route.Landing);
  }, [logout, navigate]);

  if (finalizing) {
    return <OnboardingLoading title={Localized.SharedSetUpBilling} note={Localized.SetupBillingFinalizing} />;
  }

  // The summary bullets quote the resolved base price; a zero base fee reads FREE rather than a $0.00 price.
  const planFree = plan ? ResolvePlanBase(plan, billingCountry).amountMinor === 0 : false;

  return (
    <Card title={Localized.SharedSetUpBilling} className={Styles.onboardingCard}>
      <Label classNameContainer={Styles.muted} text={Localized.SetupBillingSubtitle} />

      {plan ? (
        <div className={Styles.field}>
          <Label classNameContainer={Styles.label} text={Localized.SetupBillingPlanLabel} />
          {/* Raw <ul>/<li>: engine Label renders text only, and the multi-line plan summary needs one bullet per line. unitStack matches the Billing plan-details feature list. */}
          <ul className={Styles.unitStack}>
            <li>
              <Label text={plan.name} />
            </li>
            <li>
              <Label text={FormatLocalized(Localized.SetupBillingPlanTrial, CommonConstants.Billing.TrialDays)} />
            </li>
            <li>
              <Label text={FormatLocalized(Localized.SetupBillingPlanPrice, planFree ? Localized.SharedFree : FormatPlanPrice(plan, selectedLang, billingCountry))} />
            </li>
            <li>
              <Label text={Localized.SetupBillingPlanCancel} />
            </li>
          </ul>
        </div>
      ) : null}

      {error.length > 0 ? <Label classNameContainer={Styles.errorText} text={error} /> : null}
      {notice.length > 0 ? <Label classNameContainer={Styles.muted} text={notice} /> : null}

      <div className={Styles.gateActions}>
        <Button text={Localized.DashSignOut} type="button" classNameButtonEnabled={Styles.btn} onClickHandler={() => void onLogout()} />
        <Button
          text={redirecting ? Localized.SetupBillingRedirecting : error.length > 0 ? Localized.SharedTryAgain : Localized.SharedSetUpBilling}
          disabled={redirecting || !plan}
          classNameButtonEnabled={`${Styles.btn} ${Styles.btnPrimary}`}
          classNameButtonDisabled={`${Styles.btn} ${Styles.btnPrimary}`}
          onClickHandler={() => void startCheckout()}
        />
      </div>
    </Card>
  );
}
